import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import myConversationsQuery from '../../graphql/query/myConversations.query';
import { ConversationList, ConversationView } from '../PrivateMessages';
import CloseIcon from '@mui/icons-material/Close';
import './MessagesDropdown.css';

interface MessagesDropdownProps {
  userId: number;
  darkMode?: boolean;
  onClose?: () => void;
  initialConversationId?: number | null;
  embedded?: boolean;
}

const MessagesDropdown: React.FC<MessagesDropdownProps> = ({
  userId,
  darkMode = false,
  onClose,
  initialConversationId = null,
  embedded = false,
}) => {
  const [selectedConversation, setSelectedConversation] = useState<any>(null);
  const didApplyDeepLink = useRef(false);

  const { data, loading, error, refetch } = useQuery(myConversationsQuery, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 8000,
  });

  // Only show threads that have been initiated (at least one message).
  // Empty conversations can exist after Contact Seller opens a chat without sending.
  const uniqueConversations = useMemo(() => {
    return (data?.myConversations?.nodes || []).filter(
      (c: any) =>
        Boolean(c) &&
        (Boolean(c.latestMessage) ||
          (c.privateMessagesByConversationId?.nodes?.length ?? 0) > 0)
    );
  }, [data]);

  useEffect(() => {
    didApplyDeepLink.current = false;
  }, [initialConversationId]);

  useEffect(() => {
    if (!initialConversationId || didApplyDeepLink.current || selectedConversation) return;
    const match = uniqueConversations.find((c: any) => c.id === initialConversationId);
    if (match) {
      setSelectedConversation(match);
      didApplyDeepLink.current = true;
    }
  }, [initialConversationId, uniqueConversations, selectedConversation]);

  // Keep the open thread object fresh when inbox data refreshes.
  useEffect(() => {
    if (!selectedConversation) return;
    const fresh = uniqueConversations.find((c: any) => c.id === selectedConversation.id);
    if (
      fresh &&
      (fresh.updatedAt !== selectedConversation.updatedAt ||
        fresh.latestMessage?.id !== selectedConversation.latestMessage?.id ||
        fresh.latestMessage?.readAt !== selectedConversation.latestMessage?.readAt)
    ) {
      setSelectedConversation(fresh);
    }
  }, [uniqueConversations, selectedConversation]);

  const handleSelectConversation = (conversation: any) => {
    setSelectedConversation(conversation);
  };

  const handleBack = () => {
    setSelectedConversation(null);
    refetch();
  };

  const handleMessageSent = () => {
    refetch();
  };

  if (error) {
    return (
      <div className={`messages-dropdown ${darkMode ? 'dark' : ''} ${embedded ? 'embedded' : ''}`}>
        <div className="messages-dropdown-header">
          <h3>Messages</h3>
          {onClose && (
            <button type="button" className="messages-dropdown-close" onClick={onClose} aria-label="Close">
              <CloseIcon fontSize="small" />
            </button>
          )}
        </div>
        <div className="messages-dropdown-error">
          <p>Error loading conversations</p>
          <span>{error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`messages-dropdown ${darkMode ? 'dark' : ''} ${embedded ? 'embedded' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="messages-dropdown-header">
        <h3>{selectedConversation ? 'Conversation' : 'Messages'}</h3>
        {onClose && (
          <button type="button" className="messages-dropdown-close" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </button>
        )}
      </div>

      <div className="messages-dropdown-content">
        {loading && uniqueConversations.length === 0 ? (
          <div className="messages-dropdown-empty">
            <p>Loading conversations…</p>
          </div>
        ) : uniqueConversations.length === 0 ? (
          <div className="messages-dropdown-empty">
            <p>No conversations yet</p>
            <span>Start by contacting a seller on a listing</span>
          </div>
        ) : selectedConversation ? (
          <ConversationView
            conversation={selectedConversation}
            currentUserId={userId}
            onBack={handleBack}
            darkMode={darkMode}
            onMessageSent={handleMessageSent}
          />
        ) : (
          <ConversationList
            conversations={uniqueConversations}
            currentUserId={userId}
            selectedConversationId={null}
            onSelectConversation={handleSelectConversation}
            darkMode={darkMode}
          />
        )}
      </div>
    </div>
  );
};

export default MessagesDropdown;
